import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast as customToast } from '@/components/custom-toast'
import { useMutation } from '@tanstack/react-query'
import { removePaymentMethodOrganizationOrganizationIdDeletePaymentMethodPaymentMethodIdDeleteMutation } from '@/generated/@tanstack/react-query.gen'
import { useUser } from '@/pages/enterprise/user-provider'

interface Props {
  open: boolean
  paymentMethodId: string
  onOpenChange: () => void
}

export default function PaymentDialogRemovePaymentMethod({
  open,
  onOpenChange,
  paymentMethodId,
}: Props) {
  const { organizationId, onUpdateSeats } = useUser()

  const { mutate, isPending } = useMutation({
    ...removePaymentMethodOrganizationOrganizationIdDeletePaymentMethodPaymentMethodIdDeleteMutation(
      {}
    ),
    onSuccess: () => {
      customToast.success({
        message: 'Payment method removed.',
      })
      onUpdateSeats()
      onOpenChange()
    },
    onError: () => {
      customToast.error({
        message: 'Failed to remove payment method. Please try again.',
      })
    },
  })

  const onConfirmRemove = () => {
    mutate({
      path: {
        organization_id: organizationId,
        payment_method_id: paymentMethodId,
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        className="max-w-2xl"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Remove Payment Method</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove this payment method? This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onOpenChange} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirmRemove}
            disabled={isPending}
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                <span>Removing...</span>
              </div>
            ) : (
              'Remove'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
